import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../../hooks/useApiCall';
import useErrors from '../../../../../../hooks/useErrors';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import WorkplacesService from '../../../../../../services/WorkplacesService';
import formatCurrency from '../../../../../../utils/formatCurrency';
import isValueValid from '../../../../../../utils/isValueValid';
import onlyNumbers from '../../../../../../utils/onlyNumbers';
import parseCurrencyStringToFloat from '../../../../../../utils/parseCurrencyStringToFloat';
import { TransfersBehaviorType } from '../../../../../Company/Settings/types';

export default function useInputChanges() {
  const [name, setName] = useState('');
  const [workplaceCode, setWorkplaceCode] = useState('');
  const [hasAffreightment, setHasAffreightment] = useState(false);
  const [operator, setOperator] = useState({ value: '', label: 'Selecione uma operadora'});
  const [usesVtAndAffreightment, setUsesVtAndAffreightment] = useState(false);

  const [cep, setCep] = useState('');
  const [streetName, setStreetName] = useState('');
  const [number, setNumber] = useState<number | string>('');
  const [complement, setComplement] = useState('');
  const [district, setDistrict] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [isGettingCepInfo, setIsGettingCepInfo] = useState(false);
  const [lat, setLat] = useState<number | string>('');
  const [lng, setLng] = useState<number | string>('');

  const [selectedWalkDistance, setSelectedWalkDistance] = useState([500, 500]);
  const [walkDistance, setWalkDistance] = useState([500, 500]);
  const [compareWithDesiredVtValue, setCompareWithDesiredVtValue] = useState(false);
  const [desiredVtValue, setDesiredVtValue] = useState('');
  const [sameWalkDistanceFromCompany, setSameWalkDistanceFromCompany] = useState(true);
  const [sameDesiredVtValueFromCompany, setSameDesiredVtValueFromCompany] = useState(true);
  const [hasKmLimit, setHasKmLimit] = useState(false);
  const [selectedKmLimit, setSelectedKmLimit] = useState([5, 5]);
  const [kmLimit, setKmLimit] = useState([5, 5]);
  const [kindOfVtSpTrans, setKindOfVtSpTrans] = useState<'common' | 'vt'>('vt');
  const [integratesWithRiocard, setIntegratesWithRiocard] = useState(true);
  const [transfersBehavior, setTransfersBehavior] = useState<TransfersBehaviorType>('default');
  const [companyTransfersBehavior, setCompanyTransfersBehavior] = useState<TransfersBehaviorType>('default');
  const [sameTransfersBehaviorFromCompany, setSameTransfersBehaviorFromCompany] = useState(true);

  const [isActive, setIsActive] = useState(true);

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const { apiCall } = useApiCall();
  const { companyCode, userCode, userProfile } = useGetGeneralAppData();

  function handleNameChange(event: ChangeEvent<HTMLInputElement>) {
    setName(event.target.value);
    if (!event.target.value) {
      setError({ field: 'name', message: 'Denominação é obrigatória!' });
    } else {
      removeError('name');
    }
  }

  function handleWorkplaceCodeChange(event: ChangeEvent<HTMLInputElement>) {
    setWorkplaceCode(event.target.value);
    if (!event.target.value) {
      setError({ field: 'workplaceCode', message: 'Código é obrigatório!' });
    } else {
      removeError('workplaceCode');
    }
  }

  function handleLatChange(value: string) {
    setLat(value ? Number(onlyNumbers(value).toFixed(6)) : '');
    console.log('handleLatChange');
    console.log(value ? Number(onlyNumbers(value).toFixed(6)) : '');
  }

  function handleLngChange(value: string) {
    setLng(value ? Number(onlyNumbers(value).toFixed(6)) : '');
    console.log('handleLngChange');
    console.log(value ? Number(onlyNumbers(value).toFixed(6)) : '');
  }

  function handleCepChange(value: string) {
    setCep(value);
  }

  function handleStreetNameChange(value: string) {
    setStreetName(value);
  }

  function handleNumberChange(value: number | string) {
    setNumber(value);
  }

  function handleDistrictChange(value: string) {
    setDistrict(value);
  }

  function handleCityChange(value: string) {
    setCity(value);
  }

  function handleUfChange(value: string) {
    setUf(value);
  }

  const handleSameWalkDistanceFromCompanyChange = useCallback((willUseCompanyValue: boolean) => {
    setSameWalkDistanceFromCompany(willUseCompanyValue);
  }, []);

  const handleSameDesiredVtValueFromCompanyChange = useCallback((willUseCompanyValue: boolean) => {
    setSameDesiredVtValueFromCompany(willUseCompanyValue);
  }, []);

  const handleSameTransfersBehaviorFromCompanyChange = useCallback((willUseCompanyValue: boolean) => {
    setSameTransfersBehaviorFromCompany(willUseCompanyValue);
  }, []);

  const handleKmLimitSelection = useCallback((km: number[]) => {
    setKmLimit(km);
  }, []);

  const checkIfWorkplaceCodeAlreadyExists = useCallback(async () => {
    if (workplaceCode) {
      await apiCall({
        apiToCall: WorkplacesService.checkIfWorkplaceCodeAlreadyExists,
        reqBody: JSON.stringify({
          codEmpresa: companyCode,
          codUsu: userCode,
          perfilUsu: userProfile,
          tipoEnd: 4,
          codEndNoclienteAlfaN: workplaceCode,
        }),
        actionAfterResponse: (response) => {
          if (!response.codigo) {
            setWorkplaceCode('');
            toast.error('Houve um erro ao validar o código. Por favor, tente novamente');
            return;
          }
          if (!response.codEndValidado) {
            setWorkplaceCode('');
            toast.warn('Este código já está sendo usado. Você pode editar o local referente ao mesmo pesquisando-o na página anterior');
          }
        },
        catchMessage: 'Não foi possível validar o código. Por favor, tente novamente',
        catchAction: () => {
          setWorkplaceCode('');
        },
      });
    }
  }, [apiCall, companyCode, userCode, userProfile, workplaceCode]);

  const handleWalkDistanceSelection = useCallback((km: number[]) => {
    setWalkDistance(km);
  }, []);

  function handleCompareWithDesiredVtValueChange(willCompare: boolean) {
    if (willCompare) {
      setCompareWithDesiredVtValue(true);
    } else {
      removeError('desiredVtValue');
      setCompareWithDesiredVtValue(false);
    }
  }

  function handleDesiredVtValueChange(event: ChangeEvent<HTMLInputElement>) {
    setDesiredVtValue(formatCurrency(event.target.value));

    if (!event.target.value && compareWithDesiredVtValue) {
      setError({ field: 'desiredVtValue', message: 'Defina um valor máximo de VT'});
    } else if ((!isValueValid(parseCurrencyStringToFloat(formatCurrency(event.target.value)), 15, 100)) && compareWithDesiredVtValue) {
      setError({ field: 'desiredVtValue', message: `Valor deve ser entre ${formatCurrency('15,00')} e ${formatCurrency('100,00')}`});
    } else {
      removeError('desiredVtValue');
    }
  }

  return {
    name,
    getErrorMessageByFieldName,
    workplaceCode,
    handleWorkplaceCodeChange,
    handleNameChange,
    hasAffreightment,
    setHasAffreightment,
    operator,
    setOperator,
    usesVtAndAffreightment,
    setUsesVtAndAffreightment,
    cep,
    handleCepChange,
    streetName,
    number,
    handleNumberChange,
    complement,
    district,
    city,
    uf,
    isGettingCepInfo,
    lat,
    handleLatChange,
    lng,
    handleLngChange,
    errors,
    setName,
    setWorkplaceCode,
    setCep,
    setStreetName,
    setNumber,
    setComplement,
    setDistrict,
    setCity,
    setUf,
    setIsGettingCepInfo,
    setLat,
    setLng,
    checkIfWorkplaceCodeAlreadyExists,
    handleStreetNameChange,
    handleDistrictChange,
    handleCityChange,
    handleUfChange,
    setError,
    removeError,
    selectedWalkDistance,
    handleWalkDistanceSelection,
    setSelectedWalkDistance,
    compareWithDesiredVtValue,
    handleCompareWithDesiredVtValueChange,
    desiredVtValue,
    handleDesiredVtValueChange,
    walkDistance,
    setWalkDistance,
    setCompareWithDesiredVtValue,
    setDesiredVtValue,
    sameWalkDistanceFromCompany,
    handleSameWalkDistanceFromCompanyChange,
    sameDesiredVtValueFromCompany,
    handleSameDesiredVtValueFromCompanyChange,
    setSameWalkDistanceFromCompany,
    setSameDesiredVtValueFromCompany,
    hasKmLimit,
    setHasKmLimit,
    selectedKmLimit,
    setSelectedKmLimit,
    kmLimit,
    kindOfVtSpTrans,
    setKindOfVtSpTrans,
    integratesWithRiocard,
    setIntegratesWithRiocard,
    handleKmLimitSelection,
    setKmLimit,
    isActive,
    setIsActive,
    transfersBehavior,
    setTransfersBehavior,
    companyTransfersBehavior,
    setCompanyTransfersBehavior,
    sameTransfersBehaviorFromCompany,
    setSameTransfersBehaviorFromCompany,
    handleSameTransfersBehaviorFromCompanyChange,
  };
}
